import {
	createFileRoute,
	Link,
	Outlet,
	useMatches,
} from "@tanstack/react-router";
import {
	ArrowLeft,
	Check,
	Copy,
	RadioTower,
	SkipBack,
	SkipForward,
	SquareArrowOutUpRight,
} from "lucide-react";
import { useEffect, useState, useRef } from "react";
import { apiClient } from "@/lib/api-client";
import { getSoundEffects, initSoundEffects } from "@/services/SoundEffects";
import { useStationConnection } from "@/hooks/useStationConnection";
import { useDemoAudio } from "@/hooks/useDemoAudio";
import { trackStationVisit } from "@/components/FacebookPixel";
import { CreateStationCTA } from "@/components/CreateStationCTA";

export const Route = createFileRoute("/_layout/$username/$stationSlug")({
	component: StationPage,
	loader: async ({ params }) => {
		// Handle demo station as a special case
		if (params.username === "macrowave" && params.stationSlug === "demo") {
			return {
				station: {
					id: "station_demo00000000000000000000",
					name: "Apollo 11",
					slug: "demo",
					description: "Historic Apollo 11 mission audio - Demo Station",
					broadcasterId: "userdemo0000000000000000",
					broadcaster: {
						id: "userdemo0000000000000000",
						username: "macrowave",
						name: "Macrowave",
					},
					isActive: true,
					status: "live" as const,
					viewerCount: 0,
					nowPlaying: {
						track: "Apollo 11 Mission Audio",
						artist: "NASA",
						album: "Historic Spaceflight Recordings",
					},
					createdAt: new Date().toISOString(),
					lastActiveAt: new Date().toISOString(),
				},
				error: null,
			};
		}

		try {
			const station = await apiClient.users.getStationByUsernameAndSlug(
				params.username,
				params.stationSlug,
			);
			return { station, error: null };
		} catch (error) {
			return {
				station: null,
				error: error instanceof Error ? error.message : "Unknown error",
			};
		}
	},
	head: ({ params, loaderData }) => {
		const station = loaderData?.station;
		const { username, stationSlug } = params;

		const title = station
			? `${station.name} by @${station.broadcaster.username} - Macrowave`
			: `${stationSlug} - Macrowave`;

		const description = station
			? `Listen to ${station.name} by @${station.broadcaster.username} on Macrowave. ${station.description || "Live radio streaming."}`
			: `Listen to ${username}'s radio station on Macrowave`;

		const canonicalUrl = `https://macrowave.co/${username}/${stationSlug}`;

		return {
			meta: [
				{
					property: "og:title",
					content: title,
				},
				{
					property: "og:description",
					content: description,
				},
				{
					property: "og:type",
					content: "website",
				},
				{
					property: "og:url",
					content: canonicalUrl,
				},
				{
					name: "twitter:card",
					content: "summary_large_image",
				},
				{
					name: "twitter:title",
					content: title,
				},
				{
					name: "twitter:description",
					content: description,
				},
				{
					name: "twitter:url",
					content: canonicalUrl,
				},
				// Add canonical URL for SEO
				{
					name: "canonical",
					content: canonicalUrl,
				},
			],
		};
	},
});

function StationPage() {
	const { station, error } = Route.useLoaderData();
	const { username, stationSlug } = Route.useParams();

	// Detect if this is the demo station
	const isDemoStation = station?.slug === "demo" && username === "macrowave";

	// Use demo audio hook for demo station, real connection for others
	// Only initialize demo audio if it's actually the demo station (to avoid downloading audio file)
	const demoAudio = useDemoAudio({ enabled: isDemoStation });
	const liveStation = useStationConnection({
		station: isDemoStation ? (null as any) : station,
		autoConnect: !isDemoStation, // Only auto-connect for real stations
	});

	// Select the appropriate connection based on station type
	const {
		isConnected,
		isConnecting,
		error: connectionError,
		connect,
		disconnect,
		nowPlaying,
	} = isDemoStation
		? { ...demoAudio, nowPlaying: null, error: demoAudio.audioError ? new Error("Audio error") : null }
		: { ...liveStation, error: liveStation.error };

	// Initialize sound effects
	useEffect(() => {
		initSoundEffects();
	}, []);

	// Track station visit for Facebook Pixel
	useEffect(() => {
		if (station) {
			trackStationVisit({
				stationId: station.id,
				stationName: station.name,
				broadcasterUsername: station.broadcaster.username,
				stationSlug: stationSlug,
				isLive: station.isActive && station.status === "live",
				viewerCount: station.viewerCount,
			});
		}
	}, [station, stationSlug]);

	// Handle station not found
	if (error) {
		return (
			<div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
				<h1 className="mb-2 font-serif-600 text-2xl text-black">
					Station Not Found
				</h1>
				<p className="mb-6 font-serif text-gray-600">
					{error === "Station not found"
						? `The station "${stationSlug}" by @${username} doesn't exist.`
						: "There was an error loading this station."}
				</p>
				<div className="flex gap-4">
					<Link
						className="font-serif text-gray-400 hover:text-black transition-colors duration-200 cursor-pointer"
						onClick={() => getSoundEffects().play("buttonDown")}
						params={{ username }}
						to="/$username"
					>
						@{username}'s Profile
					</Link>
					<Link
						className="font-serif text-gray-400 hover:text-black transition-colors duration-200 cursor-pointer"
						onClick={() => getSoundEffects().play("buttonDown")}
						to="/stations"
					>
						Browse Stations
					</Link>
				</div>
			</div>
		);
	}

	if (!station) {
		return (
			<div className="flex min-h-[60vh] items-center justify-center">
				<div className="text-center">
					<div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-gray-300 border-b-2" />
					<p className="font-serif text-gray-600">Loading station...</p>
				</div>
			</div>
		);
	}

	const isLive = station.isActive && station.status === "live";
	const isDisconnected = station.status === "disconnected";
	const isEnded = station.status === "ended" || !station.isActive;

	// Use real-time Now Playing data if available, otherwise fall back to static data
	const displayNowPlaying = nowPlaying
		? {
				track: nowPlaying.name,
				artist: nowPlaying.artist,
				album: nowPlaying.album,
			}
		: station.nowPlaying;

	return (
		<div className="flex min-h-[60vh] items-center justify-center">
			<div className="w-full">
				{/* Broadcaster Profile Link */}
				<div className="mb-8">
					<Link
						to="/$username"
						params={{ username }}
						className="font-serif text-gray-400 hover:text-black transition-colors duration-200 cursor-pointer text-2xl flex items-center gap-2"
						onClick={() => getSoundEffects().play("buttonDown")}
					>
						<ArrowLeft className="h-5 w-5" />
						{station.broadcaster.name}
					</Link>
				</div>

				<div className="device-container">
					{/* Hardware Device Frame */}
					<div className="device-body">
						{/* Front Panel */}
						<div className="device-panel device-front">
							{/* Main Display Screen */}
							<div className="device-screen">
								<div className="bg-[#081506] h-full rounded-lg p-4 relative overflow-hidden">
									{/* Scanline effect overlay */}
									<div
										className="absolute inset-0 pointer-events-none"
										style={{
											background:
												"linear-gradient(0deg, transparent 50%, rgba(106, 223, 71, 0.02) 50%)",
											backgroundSize: "100% 3px",
											animation: "scanlines 10s linear infinite",
										}}
									/>

									<div className="text-[#6adf47] font-array space-y-4 relative z-10 h-full flex flex-col justify-center">
										{/* Station Info Section - matches spacing: 0 in Swift */}
										<div className="space-y-0">
											<h1 className="text-lg font-bold leading-tight truncate">
												{station.name}
											</h1>
											{isLive ? (
												<div className="text-sm leading-tight truncate">
													{station.viewerCount}{" "}
													{station.viewerCount === 1 ? "Listener" : "Listeners"}
												</div>
											) : (
												<div className="text-sm text-[#4fa935] leading-tight truncate">
													{isDisconnected ? "Not Connected" : "Offline"}
												</div>
											)}
											{station.description && (
												<div className="text-sm text-[#4fa935] leading-tight truncate">
													{station.description}
												</div>
											)}
										</div>

										{/* Now Playing Section - matches spacing: 0 in Swift */}
										<div className="space-y-0">
											{displayNowPlaying?.track && isLive ? (
												<>
													<div className="text-sm font-bold leading-tight truncate">
														{displayNowPlaying.track}
													</div>
													<div className="text-sm text-[#4fa935] leading-tight truncate">
														{displayNowPlaying.artist} –{" "}
														{displayNowPlaying.album}
													</div>
												</>
											) : (
												<>
													<div className="text-sm opacity-0 leading-tight">
														K
													</div>
													<div className="text-sm opacity-0 leading-tight">
														K
													</div>
												</>
											)}
										</div>
									</div>
								</div>
							</div>

							{/* Control Buttons Row */}
							<div className="device-controls">
								{/* Power Button */}
								<div className="power-button">
									<button
										className={`device-button ${isConnected ? "enabled" : ""}`}
										onClick={() => {
											getSoundEffects().play("buttonDown");
											if (!isConnected && !isConnecting) {
												connect();
											} else if (isConnected) {
												disconnect();
											}
										}}
										disabled={!isLive || isConnecting}
									>
										<div
											className={`status-led ${isConnected ? "led-on" : isConnecting ? "led-connecting" : "led-off"}`}
										/>
										<span className="button-label">Power</span>
									</button>
								</div>

								{/* Navigation Buttons */}
								<div className="nav-buttons">
									<button
										className="device-button nav-button"
										onClick={() => {
											getSoundEffects().play("buttonDown");
										}}
									>
										<span className="button-icon">
											<SkipBack className="w-4 h-4" />
										</span>
									</button>
									<button
										className="device-button nav-button"
										onClick={() => {
											getSoundEffects().play("buttonDown");
											// Station selector functionality could go here
										}}
									>
										<span className="button-icon">
											<RadioTower className="w-4 h-4" />
										</span>
									</button>
									<button
										className="device-button nav-button"
										onClick={() => {
											getSoundEffects().play("buttonDown");
										}}
									>
										<span className="button-icon">
											<SkipForward className="w-4 h-4" />
										</span>
									</button>
								</div>

								{/* Share Button */}
								<div className="share-button">
									<button
										className="device-button"
										onClick={async () => {
											getSoundEffects().play("buttonDown");

											const shareData = {
												title: `${station.name} by @${station.broadcaster.username}`,
												text:
													station.description ||
													`Listen to ${station.name} on Macrowave`,
												url: window.location.href,
											};

											if (
												navigator.share &&
												navigator.canShare &&
												navigator.canShare(shareData)
											) {
												try {
													await navigator.share(shareData);
												} catch (err) {
													// User cancelled sharing or error occurred
													console.log("Share cancelled or failed:", err);
												}
											} else {
												// Fallback: copy URL to clipboard
												try {
													await navigator.clipboard.writeText(
														window.location.href,
													);
													// Could add a toast notification here
													console.log("URL copied to clipboard");
												} catch (err) {
													console.log("Failed to copy URL:", err);
												}
											}
										}}
									>
										<span className="button-icon">
											<SquareArrowOutUpRight className="w-4 h-4" />
										</span>
									</button>
								</div>
							</div>
						</div>

						{/* Device Sides for 3D effect */}
						<div className="device-panel device-back"></div>
						<div className="device-panel device-right"></div>
						<div className="device-panel device-left"></div>
					</div>

					{/* Error Messages */}
					{connectionError && connectionError.name !== "NotAllowedError" && (
						<div className="text-center mt-6">
							<p className="font-serif text-red-600 mb-2">Connection Error</p>
							<p className="font-serif text-red-500 text-sm mb-4">
								{connectionError.name || "Failed to connect to the station"}
							</p>
							<button
								className="font-serif text-gray-400 hover:text-black transition-colors duration-200 cursor-pointer"
								onClick={() => {
									getSoundEffects().play("buttonDown");
									connect();
								}}
							>
								Try Again
							</button>
						</div>
					)}
					{connectionError && connectionError.name === "NotAllowedError" && (
						<div className="text-center mt-6">
							<button
								className="font-serif text-gray-400 hover:text-black transition-colors duration-200 cursor-pointer"
								onClick={() => {
									getSoundEffects().play("buttonDown");
									connect();
								}}
							>
								Tune In
							</button>
						</div>
					)}

					{/* Status Messages */}
					{!isLive && !connectionError && (
						<div className="text-center mt-6">
							<p className="font-serif text-gray-600 mb-2">
								{isDisconnected ? "Host disconnected" : "Station offline"}
							</p>
							<p className="font-serif text-gray-500 text-sm">
								{isDisconnected
									? "The broadcaster is reconnecting"
									: "This broadcast has ended"}
							</p>
						</div>
					)}
				</div>

				{/* CTA Section */}
				<CreateStationCTA />
			</div>
		</div>
	);
}
